import { createServerFn } from "@tanstack/react-start";

const CATEGORIES = [
  "infrastructure", "environment", "security", "healthcare", "education",
  "transportation", "water", "electricity", "waste", "governance",
  "emergency", "community",
] as const;

const SEVERITIES = ["info", "warning", "urgent", "critical"] as const;

export type ReportAnalysis = {
  problem_type: string;
  title: string;
  category: (typeof CATEGORIES)[number];
  severity: (typeof SEVERITIES)[number];
  confidence: number; // 0..1
  summary: string;
  potential_risks: string[];
  recommended_action: string;
  responsible_authority: string;
  model: string | null;
  is_fallback: boolean;
};

const SYSTEM = `You are the civic infrastructure and environmental analyst for Xamseen.
A citizen submits one or more photos of a real-world community problem plus an optional note and location.
Return STRICT JSON only, no prose, matching:
{
  "problem_type": short human label e.g. "Blocked Drainage", "Illegal Waste Dumping", "Pothole", "Fallen Utility Pole", "Stagnant Water",
  "title": a concise report headline under 80 chars,
  "category": one of ${CATEGORIES.join(", ")},
  "severity": one of info, warning, urgent, critical,
  "confidence": number between 0 and 1,
  "summary": 1-2 sentences describing what is visible,
  "potential_risks": 2-4 short risk phrases e.g. "Flooding", "Mosquito breeding", "Road accidents",
  "potential_risks_note": optional,
  "recommended_action": one clear actionable sentence,
  "responsible_authority": the category of responsible body e.g. "Municipal waste management", "Road maintenance agency", "Water & drainage utility", "Electricity distribution company", "Environmental protection agency", "Local government"
}
Guidance: drainage blockage/stagnant water => category "water" or "environment" with mosquito/flooding risks. Refuse heaps => "waste". Potholes/road damage => "transportation" or "infrastructure". Broken streetlights/fallen poles => "electricity". Air/water contamination => "environment". Immediate danger to life => severity "critical".
If the photo does not show a civic problem, use category "community", severity "info" and low confidence.`;

function fallback(description?: string | null): ReportAnalysis {
  const text = (description ?? "").trim();
  return {
    problem_type: "Unclassified problem",
    title: text ? text.split(/\s+/).slice(0, 12).join(" ").slice(0, 80) : "Community problem report",
    category: "community",
    severity: "info",
    confidence: 0,
    summary: "Automatic analysis is not configured in this environment, so this report was not assessed by AI.",
    potential_risks: [],
    recommended_action: "A community moderator should review this report manually.",
    responsible_authority: "Local government",
    model: null,
    is_fallback: true,
  };
}

export const analyzeReport = createServerFn({ method: "POST" })
  .inputValidator((data: { imageUrls: string[]; description?: string | null; locationName?: string | null }) => data)
  .handler(async ({ data }): Promise<ReportAnalysis> => {
    const apiKey = process.env.LOVABLE_API_KEY;
    if (!apiKey || data.imageUrls.length === 0) return fallback(data.description);

    const model = "google/gemini-2.5-flash";
    try {
      const res = await fetch("https://ai.gateway.lovable.dev/v1/chat/completions", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${apiKey}` },
        body: JSON.stringify({
          model,
          messages: [
            { role: "system", content: SYSTEM },
            {
              role: "user",
              content: [
                {
                  type: "text",
                  text: `Citizen note: ${data.description?.slice(0, 800) || "(none)"}\nLocation: ${data.locationName || "unknown"}`,
                },
                ...data.imageUrls.slice(0, 4).map((url) => ({ type: "image_url", image_url: { url } })),
              ],
            },
          ],
          response_format: { type: "json_object" },
        }),
      });

      if (!res.ok) {
        if (res.status === 429) throw new Error("AI analysis is rate limited right now. Please try again in a moment.");
        if (res.status === 402) throw new Error("AI analysis credits are exhausted. Please top up to continue.");
        return fallback(data.description);
      }

      const json: any = await res.json();
      const raw = json?.choices?.[0]?.message?.content;
      if (!raw) return fallback(data.description);
      const p = JSON.parse(raw);

      const confidence = Math.max(0, Math.min(1, Number(p.confidence ?? 0) > 1 ? Number(p.confidence) / 100 : Number(p.confidence ?? 0)));
      return {
        problem_type: String(p.problem_type ?? "Community problem").slice(0, 80),
        title: String(p.title ?? p.problem_type ?? "Community problem").slice(0, 140),
        category: CATEGORIES.includes(p.category) ? p.category : "community",
        severity: SEVERITIES.includes(p.severity) ? p.severity : "info",
        confidence,
        summary: String(p.summary ?? "").slice(0, 600),
        potential_risks: Array.isArray(p.potential_risks)
          ? p.potential_risks.map((r: any) => String(r).slice(0, 80)).filter(Boolean).slice(0, 5)
          : [],
        recommended_action: String(p.recommended_action ?? "").slice(0, 400),
        responsible_authority: String(p.responsible_authority ?? "Local government").slice(0, 120),
        model,
        is_fallback: false,
      };
    } catch (err: any) {
      if (typeof err?.message === "string" && err.message.startsWith("AI analysis")) throw err;
      return fallback(data.description);
    }
  });
